import { Link } from "react-router-dom";
import { Check, MapPin, Megaphone, MessageSquare, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  cardChips,
  cardMarkets,
  computeLookingForMatch,
  hasLookingForContent,
  liveAnnouncement,
  matchFooter,
  normalizeLookingFor,
  shortDate,
  type ChipResult,
} from "@/lib/lookingFor";
import { canUseLookingFor } from "@/lib/orgEntitlements";
import type { OfficesDisplay } from "@/lib/officesDisplay";

/**
 * Who is looking at the card. Decided by the page, never inferred here.
 * - agent:     a signed-in agent who is not a member — sees their private match
 * - member:    a member of this organization — no match, no contact button
 * - preview:   an org admin previewing their own story
 * - signedOut: the public page
 */
export type LookingForViewer = "agent" | "member" | "preview" | "signedOut";

interface Props {
  lookingFor: unknown;
  /** Office labels, for the markets fallback. */
  offices: string[];
  /** "states" names the markets by state, matching the Offices card. */
  officesDisplay?: OfficesDisplay;
  orgId?: string;
  viewer: LookingForViewer;
  /** The viewer's own profiles.profile_data (agent only). Never sent anywhere. */
  agentProfileData?: unknown;
  onMessage?: () => void;
  /** Where the admin fills the section in (preview only). */
  editorHref?: string;
  /** Where a signed-out visitor signs in (public page only). */
  signInHref?: string;
  /** Opens the page's sign-in box; takes precedence over signInHref (public story page). */
  onSignIn?: () => void;
  className?: string;
}

const markFor: Record<ChipResult, { icon: typeof Check; className: string; label: string }> = {
  met: { icon: Check, className: "text-primary", label: "You meet this" },
  unmet: { icon: Minus, className: "text-ink-500", label: "You don't meet this yet" },
  unknown: { icon: Minus, className: "text-gray-300", label: "Not on your profile yet" },
};

/**
 * "Who we're looking for" — the organization header card (spec 2026-09-18).
 *
 * Every organization is recruiting, so there is no hiring status. What the
 * card never does is speak for an org that has not spoken: with no headline,
 * no requirement and no explicit "open to all", it shows only where the org
 * is and how to reach it.
 */
export default function LookingForCard({
  lookingFor,
  offices,
  officesDisplay = "cities",
  orgId,
  viewer,
  agentProfileData,
  onMessage,
  editorHref,
  signInHref,
  onSignIn,
  className,
}: Props) {
  const entitled = canUseLookingFor({ id: orgId });
  const lf = entitled ? normalizeLookingFor(lookingFor) : null;
  const hasContent = hasLookingForContent(lf);
  const chips = cardChips(lf);
  const markets = cardMarkets(lf, offices, officesDisplay);
  const announcement = liveAnnouncement(lf);
  const match = viewer === "agent" ? computeLookingForMatch(chips, agentProfileData) : null;
  const footer = match ? matchFooter(match) : null;

  const showButton = viewer === "agent" ? !!onMessage : viewer === "preview" || viewer === "signedOut";
  // A member looking at an org that has said nothing gets nothing: no claim, no button.
  if (!hasContent && markets.shown.length === 0 && !showButton && viewer !== "preview") return null;

  return (
    <section
      aria-label="Who we're looking for"
      className={cn("rounded-xl border border-border bg-card p-4 sm:p-5", className)}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="eyebrow text-[10px] text-ink-500">Who we're looking for</h2>
        {hasContent && lf?.updatedAt && (
          <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-ink-500">Updated {shortDate(lf.updatedAt)}</span>
        )}
      </div>

      {lf?.headline && <p className="mt-2 text-[15px] font-semibold leading-snug text-ink-900">{lf.headline}</p>}

      {markets.shown.length > 0 && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ink-700">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" strokeWidth={1.9} />
          <span className="min-w-0">
            {markets.shown.join(", ")}
            {markets.more > 0 && ` +${markets.more} more`}
          </span>
        </p>
      )}

      {lf?.openToAll && <p className="mt-3 text-sm text-ink-700">Open to agents at every stage.</p>}

      {chips.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5" aria-label="Requirements">
          {chips.map((chip, i) => {
            const result = match?.results[i]?.result;
            const mark = result ? markFor[result] : null;
            const Icon = mark?.icon;
            return (
              <li
                key={`${chip.kind}:${chip.value}`}
                className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-1 text-xs font-medium text-ink-700"
              >
                {Icon && <Icon aria-label={mark!.label} className={cn("h-3 w-3", mark!.className)} strokeWidth={2.5} />}
                {chip.label}
              </li>
            );
          })}
        </ul>
      )}

      {viewer === "preview" && !hasContent && (
        <p className="mt-3 text-sm text-ink-700">
          Agents only see where you are.{" "}
          {editorHref && (
            <Link to={editorHref} className="font-medium text-primary underline-offset-4 hover:underline">
              Tell agents who you're looking for
            </Link>
          )}
        </p>
      )}

      {announcement && (
        <p className="mt-3 flex items-start gap-1.5 border-l-2 border-featured pl-2 text-[13px] text-ink-700">
          <Megaphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-500" />
          {announcement.text}
        </p>
      )}

      {(showButton || chips.length > 0) && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
          <div className="min-w-0 text-xs">
            {footer?.kind === "match" && (
              <span className="font-semibold text-primary">
                {footer.text}
                {footer.addDetails && (
                  <>
                    <span className="font-normal text-ink-500"> · </span>
                    <Link to="/agent/profile" className="font-medium text-ink-700 underline-offset-4 hover:underline">
                      Add your production details
                    </Link>
                  </>
                )}
              </span>
            )}
            {footer?.kind === "addGoals" && (
              <Link to="/agent/profile" className="font-medium text-primary underline-offset-4 hover:underline">
                {footer.text}
              </Link>
            )}
            {viewer === "preview" && chips.length > 0 && <span className="text-ink-500">Agents see their match here</span>}
            {viewer === "signedOut" && chips.length > 0 && <span className="text-ink-500">Sign in to see your fit</span>}
          </div>
          {viewer === "agent" && onMessage && (
            <Button size="sm" className="gap-1.5" onClick={onMessage}>
              <MessageSquare className="h-3.5 w-3.5" /> Start a conversation
            </Button>
          )}
          {viewer === "preview" && (
            <Button size="sm" className="pointer-events-none gap-1.5 opacity-60" tabIndex={-1} aria-disabled>
              <MessageSquare className="h-3.5 w-3.5" /> Start a conversation
            </Button>
          )}
          {viewer === "signedOut" && onSignIn && (
            <Button size="sm" className="gap-1.5" onClick={onSignIn}>
              <MessageSquare className="h-3.5 w-3.5" /> Start a conversation
            </Button>
          )}
          {viewer === "signedOut" && !onSignIn && signInHref && (
            <Button size="sm" asChild>
              <Link to={signInHref}>Sign in to connect</Link>
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
